<template>
  <div class="iqb">
    <!-- toolbar -->
    <div class="iqb-bar">
      <button type="button" class="iq-chip" :class="{ 'iq-chip--on': mine }" @click="mine = !mine">
        Asignadas a mí
      </button>
      <span class="iqb-bar__meta">{{ totalOpen }} en el embudo</span>
      <button type="button" class="iqb-refresh" title="Actualizar" @click="fetchBoard">
        <hero-icon :icon="RefreshIcon" :size="15" />
      </button>
    </div>

    <!-- columns — sized to fill the viewport exactly (measured, no magic offsets) -->
    <div ref="cols" class="iqb-cols" :style="{ height: colsHeight }">
      <div
        v-for="col in columns"
        :key="col.state"
        class="iqb-col"
        :class="{
          'iqb-col--terminal': col.terminal,
          'iqb-col--rail': isRail(col),
          'iqb-col--over': dragOver === col.state
        }"
        @dragover.prevent="dragOver = col.state"
        @dragleave="dragOver === col.state && (dragOver = null)"
        @drop.prevent="onDrop(col)"
      >
        <!-- collapsed terminal rail: outcomes stay on-screen, one click to expand,
             still a drop target -->
        <button
          v-if="isRail(col)"
          type="button"
          class="iqb-rail"
          :style="{ borderTopColor: col.color }"
          @click="expandCol(col.state)"
        >
          <span class="iqb-rail__count">{{ col.count }}</span>
          <span class="iqb-rail__label">{{ col.label }}</span>
        </button>

        <template v-else>
          <div class="iqb-col__head" :style="{ borderTopColor: col.color }">
            <span class="iqb-col__title">{{ col.label }}</span>
            <button
              v-if="col.terminal"
              type="button"
              class="iqb-col__collapse"
              title="Colapsar"
              @click="collapseCol(col.state)"
            >×</button>
            <span class="iqb-col__count">{{ col.count }}</span>
          </div>

          <div class="iqb-col__cards">
            <div
              v-for="card in col.inquiries"
              :key="card.id"
              class="iqb-card"
              :class="slaClass(col, card)"
              draggable="true"
              @dragstart="onDragStart($event, card, col)"
              @dragend="dragging = null; dragOver = null"
              @click="openDetail(card)"
            >
              <div class="iqb-card__top">
                <span class="iqb-card__name">
                  <span v-if="card.read === false" class="iqb-card__dot" title="Actividad sin leer"></span>
                  {{ displayName(card.name) || "Consulta" }}
                </span>
                <span
                  v-if="card.leadTier"
                  class="iqb-card__temp"
                  :class="'iqb-card__temp--' + card.leadTier"
                >{{ tierLabels[card.leadTier] || card.leadTier }}<template v-if="card.leadScore != null"> · {{ card.leadScore }}</template></span>
              </div>

              <div v-if="subject(card)" class="iqb-card__prop">{{ subject(card) }}</div>

              <div class="iqb-card__meta">
                <!-- Nueva speaks in waiting time (the SLA); the rest in time-in-stage -->
                <span v-if="col.state === 'pending'" class="iqb-card__stage">
                  esperando {{ timeIn(card.createdAtTime || card.stateChangedAt) }}
                </span>
                <span v-else class="iqb-card__stage" :title="'En esta etapa desde ' + fullDate(card.stateChangedAt)">
                  {{ timeIn(card.stateChangedAt) }} en etapa
                </span>
                <span v-if="card.manual" class="iqb-card__origin">Manual</span>
              </div>

              <div
                v-if="card.nextActionAt && !col.terminal"
                class="iqb-card__next"
                :class="'iqb-card__next--' + dueness(card.nextActionAt)"
                :title="card.nextActionNote || ''"
              >
                ⏰ {{ nextLabel(card.nextActionAt) }}<template v-if="card.nextActionNote"> — {{ card.nextActionNote }}</template>
              </div>
              <!-- a missing follow-up is only worth flagging once the lead is being worked -->
              <div
                v-else-if="!col.terminal && col.state !== 'pending'"
                class="iqb-card__nonext"
              >sin seguimiento programado</div>

              <div v-if="col.state === 'lost' && card.lostReason" class="iqb-card__reason">
                {{ lostReasonLabel(card.lostReason) }}
              </div>
            </div>

            <button
              v-if="col.count > col.inquiries.length"
              type="button"
              class="iqb-col__more"
              @click="openList(col.state)"
            >
              +{{ col.count - col.inquiries.length }} más — ver en Lista
            </button>
            <button
              v-if="col.staleCount"
              type="button"
              class="iqb-col__stale"
              title="Consultas de más de 45 días que nunca se trabajaron"
              @click="openList(col.state)"
            >
              {{ col.staleCount }} antiguas sin trabajar — ver en Lista
            </button>
            <div v-if="!col.inquiries.length" class="iqb-col__empty">{{ emptyHint(col) }}</div>
          </div>
        </template>
      </div>
    </div>

    <!-- lost reason prompt (on drop into Perdida) -->
    <v-dialog v-model="lostDialog" width="380" content-class="inquiry-dialog">
      <v-card flat class="iqb-lost">
        <div class="iqb-lost__title">¿Por qué se perdió?</div>
        <div class="iqb-lost__sub">{{ lostCard && lostCard.name }}</div>
        <button
          v-for="r in lostReasons"
          :key="r.value"
          type="button"
          class="iqb-lost__opt"
          @click="confirmLost(r.value)"
        >{{ r.label }}</button>
        <button type="button" class="iqb-lost__cancel" @click="lostDialog = false">Cancelar</button>
      </v-card>
    </v-dialog>

    <inquiry-detail-dialog ref="detail" @reload="fetchBoard" />
  </div>
</template>

<script>
import { InquiryBoard } from "@/graphql/queries/inquiries";
import { subscribeInquiries } from "@/services/inquiriesCable";
import { UpdateInquiryState } from "@/graphql/mutations/inquiries";
import { FUNNEL, LOST_REASONS, lostReasonLabel } from "@/utils/funnel";
import HeroIcon from "@/components/shared/HeroIcon";
import InquiryDetailDialog from "@/components/inquiries/InquiryDetailDialog";
import RefreshIcon from "heroicons/24/outline/arrow-path.svg";

const EMPTY_HINTS = {
  pending: "Sin consultas nuevas — bandeja al día.",
  ongoing: "Sin conversaciones activas.",
  on_hold: "Nada en seguimiento.",
  visit: "Arrastrá acá cuando agenden una visita.",
  negotiation: "Acá viven las ofertas y reservas.",
  success: "Sin operaciones ganadas en 90 días.",
  lost: "Sin perdidas en 90 días."
};

export default {
  name: "InquiryBoard",

  components: { HeroIcon, InquiryDetailDialog },

  data: () => ({
    board: [],
    mine: false,
    loading: false,
    dragging: null, // { id, from, card }
    dragOver: null,
    expanded: {}, // terminal columns start collapsed (rails)
    boardTop: 230, // real value measured on mount
    lostDialog: false,
    lostCard: null,
    lostReasons: LOST_REASONS,
    tierLabels: { hot: "Caliente", warm: "Tibio", cold: "Frío" },
    RefreshIcon
  }),

  computed: {
    columns() {
      return FUNNEL.map(def => {
        const data = this.board.find(c => c.state === def.state) || { count: 0, staleCount: 0, inquiries: [] };
        return { ...def, count: data.count, staleCount: data.staleCount || 0, inquiries: data.inquiries };
      });
    },

    totalOpen() {
      return this.columns.filter(c => !c.terminal).reduce((n, c) => n + c.count, 0);
    },

    colsHeight() {
      return `calc(100vh - ${this.boardTop + 14}px)`;
    }
  },

  watch: {
    mine() {
      this.fetchBoard();
    }
  },

  created() {
    this.fetchBoard();
    // live: consultas move / arrive / get replies from other seats or the site.
    // Debounced (events can come in small bursts) and never mid-drag.
    this._sub = subscribeInquiries(() => {
      clearTimeout(this._liveTimer);
      this._liveTimer = setTimeout(() => {
        if (!this.dragging) this.fetchBoard();
      }, 400);
    });
  },

  mounted() {
    this.measure();
    window.addEventListener("resize", this.measure);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.measure);
    clearTimeout(this._liveTimer);
    if (this._sub) this._sub.unsubscribe();
  },

  methods: {
    // the board fills whatever is left below the header + toolbar
    measure() {
      this.$nextTick(() => {
        const el = this.$refs.cols;
        if (el) this.boardTop = Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY));
      });
    },

    lostReasonLabel,

    fetchBoard() {
      this.loading = true;
      this.$apollo
        .query({
          query: InquiryBoard,
          variables: { mine: this.mine, perColumn: 30 },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          this.board = res.data.inquiryBoard || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // terminal columns collapse to rails; dragging over one auto-expands intent
    isRail(col) {
      return col.terminal && !this.expanded[col.state] && this.dragOver !== col.state;
    },

    expandCol(state) {
      this.$set(this.expanded, state, true);
    },

    collapseCol(state) {
      this.$set(this.expanded, state, false);
    },

    emptyHint(col) {
      return EMPTY_HINTS[col.state] || "—";
    },

    // legacy rows arrive SHOUTING — title-case fully-uppercased names for display
    displayName(name) {
      const n = (name || "").trim();
      if (n.length > 3 && n === n.toUpperCase()) {
        return n.toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());
      }
      return n;
    },

    subject(card) {
      const extra = (card.properties || []).length - 1;
      const base = card.property ? card.property.fullAddress : null;
      if (!base) return extra > 0 ? `Paquete · ${extra + 1} propiedades` : "";
      return extra > 0 ? `${base} (+${extra})` : base;
    },

    openList(state) {
      this.$emit("open-list", state);
    },

    onDragStart(e, card, col) {
      this.dragging = { id: card.id, from: col.state, card };
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", card.id);
    },

    onDrop(col) {
      const d = this.dragging;
      this.dragOver = null;
      if (!d || d.from === col.state) return;

      if (col.state === "lost") {
        this.lostCard = d.card;
        this.lostDialog = true;
        return;
      }
      this.moveCard(d.card, d.from, col.state);
    },

    confirmLost(reason) {
      this.lostDialog = false;
      if (!this.lostCard) return;
      const from = this.dragging ? this.dragging.from : this.lostCard.state;
      this.moveCard(this.lostCard, from, "lost", reason);
      this.lostCard = null;
    },

    // optimistic column move, then persist (refetch on failure)
    moveCard(card, from, to, lostReason = null) {
      const src = this.board.find(c => c.state === from);
      const dst = this.board.find(c => c.state === to);
      if (src) {
        src.inquiries = src.inquiries.filter(i => i.id !== card.id);
        src.count = Math.max(0, src.count - 1);
      }
      if (dst) {
        dst.inquiries = [{ ...card, state: to, lostReason, stateChangedAt: new Date().toISOString() }, ...dst.inquiries];
        dst.count += 1;
      }
      this.dragging = null;

      this.$apollo
        .mutate({
          mutation: UpdateInquiryState,
          variables: { input: { id: card.id, state: to, lostReason } }
        })
        .catch(() => this.fetchBoard());
    },

    openDetail(card) {
      this.$refs.detail.open(card.id);
    },

    // SLA spine on Nueva: waiting >2h amber, >24h red
    slaClass(col, card) {
      if (col.state !== "pending") return null;
      const h = (Date.now() - new Date(card.createdAtTime || card.lastActivityAt).getTime()) / 3600000;
      if (h >= 24) return "iqb-card--sla-red";
      if (h >= 2) return "iqb-card--sla-amber";
      return null;
    },

    dueness(iso) {
      const d = new Date(iso);
      const today = new Date();
      if (d.getTime() < Date.now() && d.toDateString() !== today.toDateString()) return "overdue";
      if (d.toDateString() === today.toDateString()) return "today";
      return "future";
    },

    nextLabel(iso) {
      const due = this.dueness(iso);
      if (due === "today") return "hoy";
      const d = new Date(iso);
      const label = d.toLocaleDateString("es-AR", { day: "numeric", month: "short" });
      return due === "overdue" ? `venció ${label}` : label;
    },

    timeIn(iso) {
      if (!iso) return "—";
      const m = Math.floor(Math.max(0, Date.now() - new Date(iso).getTime()) / 60000);
      if (m < 60) return `${m} min`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h} h`;
      const d = Math.floor(h / 24);
      if (d < 45) return `${d} d`;
      if (d < 365) return `${Math.floor(d / 30)} meses`;
      return "+1 año";
    },

    fullDate(iso) {
      return iso ? new Date(iso).toLocaleDateString("es-AR", { day: "numeric", month: "short", year: "numeric" }) : "";
    }
  }
};
</script>

<style scoped>
.iqb { padding-top: 4px; }

.iqb-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.iq-chip {
  font-size: 12px; font-weight: 600; color: #6a645a; background: #fff;
  border: 1px solid #e7e2d9; border-radius: 999px; padding: 5px 13px; cursor: pointer;
}
.iq-chip--on { background: #1a1814; color: #fff; border-color: #1a1814; }
.iqb-bar__meta { font-size: 12px; color: #a39a8c; }
.iqb-refresh {
  margin-left: auto; display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border: 1px solid #e7e2d9; border-radius: 8px;
  background: #fff; color: #6a645a; cursor: pointer;
}
.iqb-refresh:hover { color: #1a1814; }

/* columns — container height is set inline (measured to the viewport) */
.iqb-cols {
  display: flex; gap: 10px; align-items: flex-start;
  overflow-x: auto; overflow-y: hidden;
  padding-bottom: 6px;
  scrollbar-width: thin; scrollbar-color: #d9d1c2 transparent;
}
.iqb-cols::-webkit-scrollbar { height: 8px; }
.iqb-cols::-webkit-scrollbar-thumb { background: #d9d1c2; border-radius: 4px; }
.iqb-cols::-webkit-scrollbar-track { background: transparent; }
.iqb-col {
  flex: 1 1 210px; max-width: 280px; min-width: 200px;
  background: #f4f1ea; border: 1px solid #ece7de; border-radius: 12px;
  display: flex; flex-direction: column; max-height: 100%;
  transition: flex-basis 0.15s ease;
}
.iqb-col--terminal { background: #f0ede7; }
.iqb-col--rail { flex: 0 0 46px; min-width: 46px; max-width: 46px; }
.iqb-col--over { border-color: #b52118; box-shadow: 0 0 0 1px #b52118 inset; }

/* collapsed terminal rail */
.iqb-rail {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  border: none; background: none; cursor: pointer;
  padding: 10px 0 14px; border-top: 3px solid transparent; border-radius: 12px 12px 0 0;
  min-height: 150px;
}
.iqb-rail__count {
  font-size: 11px; font-weight: 700; color: #6a645a; background: #fff;
  border-radius: 999px; padding: 1px 7px;
}
.iqb-rail__label {
  writing-mode: vertical-rl; font-size: 11.5px; font-weight: 700;
  letter-spacing: 0.08em; color: #6a645a;
}

.iqb-col__head {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 12px; border-top: 3px solid transparent; border-radius: 12px 12px 0 0;
}
.iqb-col__title { flex: 1; font-size: 12.5px; font-weight: 700; color: #1a1814; }
.iqb-col__collapse {
  border: none; background: none; color: #a39a8c; font-size: 16px; line-height: 1;
  cursor: pointer; padding: 0 2px;
}
.iqb-col__collapse:hover { color: #1a1814; }
.iqb-col__count {
  font-size: 11px; font-weight: 700; color: #6a645a; background: #fff;
  border-radius: 999px; padding: 1px 8px;
}

.iqb-col__cards {
  overflow-y: auto; min-height: 0; padding: 2px 8px 10px;
  display: flex; flex-direction: column; gap: 7px;
  scrollbar-width: thin; scrollbar-color: #d9d1c2 transparent;
}
.iqb-col__cards::-webkit-scrollbar { width: 6px; }
.iqb-col__cards::-webkit-scrollbar-thumb { background: #d9d1c2; border-radius: 3px; }
.iqb-col__cards::-webkit-scrollbar-track { background: transparent; }

.iqb-col__more,
.iqb-col__stale {
  border: none; background: none; text-align: center; cursor: pointer;
  font-size: 11.5px; font-weight: 600; color: #8a5a00; padding: 5px 0 2px;
}
.iqb-col__stale { color: #a39a8c; font-weight: 500; }
.iqb-col__more:hover, .iqb-col__stale:hover { text-decoration: underline; }
.iqb-col__empty { text-align: center; font-size: 12px; color: #a39a8c; padding: 18px 14px; line-height: 1.5; }

/* cards */
.iqb-card {
  background: #fff; border: 1px solid #ece7de; border-left-width: 3px; border-radius: 10px;
  padding: 9px 11px; cursor: grab;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.iqb-card:hover { box-shadow: 0 8px 20px -14px rgba(40, 33, 22, 0.3); border-color: #dcd4c6; }
.iqb-card:active { cursor: grabbing; }
.iqb-card--sla-amber { border-left-color: #e0892f; }
.iqb-card--sla-red { border-left-color: #b52118; }

.iqb-card__top { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
.iqb-card__name {
  font-size: 12.5px; font-weight: 700; color: #1a1814;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.iqb-card__dot {
  display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: #b52118; margin-right: 4px; vertical-align: 1px;
}
.iqb-card__temp { flex: none; font-size: 9.5px; font-weight: 700; border-radius: 999px; padding: 2px 7px; }
.iqb-card__temp--hot { background: #fde3e1; color: #b52118; }
.iqb-card__temp--warm { background: #fdeecf; color: #8a5a00; }
.iqb-card__temp--cold { background: #e6edf3; color: #43596b; }

.iqb-card__prop {
  font-size: 11.5px; color: #56504a; margin-top: 3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.iqb-card__meta { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
.iqb-card__stage { font-size: 10.5px; color: #a39a8c; }
.iqb-card__origin { font-size: 9.5px; font-weight: 700; color: #6a645a; background: #f1ece3; border-radius: 999px; padding: 1px 7px; }

.iqb-card__next {
  margin-top: 6px; font-size: 10.5px; font-weight: 600; border-radius: 7px; padding: 3px 8px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.iqb-card__next--overdue { background: #fde3e1; color: #b52118; }
.iqb-card__next--today { background: #fdeecf; color: #8a5a00; }
.iqb-card__next--future { background: #f1ece3; color: #6a645a; }
.iqb-card__nonext { margin-top: 6px; font-size: 10px; color: #a39a8c; }
.iqb-card__reason { margin-top: 6px; font-size: 10.5px; color: #6a645a; background: #f1ece3; border-radius: 7px; padding: 3px 8px; }

/* lost reason dialog */
.iqb-lost { padding: 18px 18px 14px; }
.iqb-lost__title { font-size: 15px; font-weight: 700; color: #1a1814; }
.iqb-lost__sub { font-size: 12px; color: #a39a8c; margin: 2px 0 12px; }
.iqb-lost__opt {
  display: block; width: 100%; text-align: left; background: #faf8f4;
  border: 1px solid #ece7de; border-radius: 9px; padding: 9px 12px; margin-bottom: 6px;
  font-size: 13px; color: #1a1814; cursor: pointer;
}
.iqb-lost__opt:hover { border-color: #b52118; }
.iqb-lost__cancel {
  display: block; width: 100%; background: none; border: none; padding: 8px;
  font-size: 12.5px; color: #a39a8c; cursor: pointer;
}
</style>
